import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { useApi } from '../../../shared/context';
import { DealPartnerContact } from '../../../shared/models';
import * as Yup from 'yup';
import { FormikUtils } from '../../../shared/utils';
import { QueryKeys } from '../../../shared/hooks/Queries';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getApiResponseData } from '../../../shared/utils/axios';
import { Skeleton, Grid } from '@mui/material';
import { Formik, Field } from 'formik';
import {
	CerebroDrawer,
	CerebroHeading,
	CerebroText,
	CerebroForm,
	CerebroLabel,
	CerebroInput,
	CerebroYesNo,
	CerebroPrimaryButton,
} from '../../../shared/components';
import { useDealPartnerContactsQuery } from '../../../shared/hooks/Queries/useDealPartnerContactsQuery';
import { AvailableContactList } from './AvailableContactList';

interface AddDataRoomPartnerContactFormProps {
	dataRoomGuid: string | undefined;
	dealPartnerGuid: string | undefined;
	contacts: DealPartnerContact[] | null | undefined;
	close: () => void;
	open: boolean;
}

export const AddDataRoomPartnerContactForm: React.FC<AddDataRoomPartnerContactFormProps> = ({
	dataRoomGuid,
	dealPartnerGuid,
	contacts,
	open,
	close,
}) => {
	const api = useApi();
	const client = useQueryClient();
	const snackbar = useSnackbar();
	const dealPartnerContactsQuery = useDealPartnerContactsQuery(dealPartnerGuid);

	const addPartnerContactMutation = useMutation(
		async ({ values }: AddPartnerContactFormVariables) => {
			const contactToAdd = {
				dealPartnerGuid: dealPartnerGuid,
				firstName: values.firstName,
				lastName: values.lastName,
				emailAddress: values.email,
				phone: values.phone,
				sendInvite: values.sendInvite,
			};

			const { data, error } = await getApiResponseData<DealPartnerContact>(
				api.post(CerebroApiEndpoints.DataRoomPartnerContacts.Contacts(dataRoomGuid), contactToAdd)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				snackbar.enqueueSnackbar('Successfully added contact to data room.', { variant: 'success' });
				client.invalidateQueries(QueryKeys.DataRoomPartnerContacts.Root(dealPartnerGuid));
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer
			open={open}
			onClose={() => close()}
			PaperProps={{
				sx:
					dealPartnerContactsQuery.isSuccess && dealPartnerContactsQuery.data.length > 0
						? { width: '700px' }
						: { width: '500px' },
			}}
		>
			<CerebroHeading>Add Deal Partner Contact to Data Room</CerebroHeading>
			<CerebroText variant="hint">
				Access to this deal is restricted to the deal partner contacts listed below. Use the buttons below to
				manage access for existing contacts, or add new contacts. When adding a contact you can determine
				whether a contact has view only permissions or access to add and modify deal information.
			</CerebroText>

			{dealPartnerContactsQuery.isLoading && (
				<>
					<Skeleton height={60} />
					<Skeleton height={60} />
					<Skeleton height={60} />
				</>
			)}

			{dealPartnerContactsQuery.isSuccess && (
				<AvailableContactList
					dataRoomGuid={dataRoomGuid}
					dealPartnerGuid={dealPartnerGuid}
					contacts={getAvailableContacts(dealPartnerContactsQuery.data, contacts)}
				/>
			)}

			<Formik
				validationSchema={AddPartnerContactSchema}
				initialValues={{
					firstName: '',
					lastName: '',
					email: '',
					phone: '',
					sendInvite: false,
				}}
				onSubmit={FormikUtils.MutateOnSubmit(addPartnerContactMutation.mutate)}
			>
				{({ isSubmitting, handleSubmit }) => {
					return (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={8}>
									<CerebroLabel required id="firstName">
										First Name
									</CerebroLabel>
									<Field id="firstName" as={CerebroInput} name="firstName" disabled={isSubmitting} />
								</Grid>

								<Grid item xs={8}>
									<CerebroLabel required id="lastName">
										Last Name
									</CerebroLabel>
									<Field id="lastName" as={CerebroInput} name="lastName" disabled={isSubmitting} />
								</Grid>

								<Grid item xs={8}>
									<CerebroLabel required id="email">
										Email
									</CerebroLabel>
									<Field id="email" as={CerebroInput} name="email" disabled={isSubmitting} />
								</Grid>

								<Grid item xs={8}>
									<CerebroLabel id="phone">Phone</CerebroLabel>
									<Field id="phone" as={CerebroInput} name="phone" disabled={isSubmitting} />
								</Grid>

								<Grid item xs={8}>
									<CerebroLabel id="sendInvite">Send Invite</CerebroLabel>
									<Field
										id="sendInvite"
										as={CerebroYesNo}
										name="sendInvite"
										disabled={isSubmitting}
									/>
								</Grid>

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Add Contact
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type AddPartnerContactFormVariables = FormikUtils.MutateOnSubmitVariables<AddPartnerContactFormValues>;
interface AddPartnerContactFormValues {
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	sendInvite: boolean;
}

const AddPartnerContactSchema = Yup.object().shape({
	firstName: Yup.string().required('first name is required'),
	lastName: Yup.string().required('last name is required'),
	email: Yup.string().email('invalid email address').required('email address is required'),
});

function getAvailableContacts(
	partnerContacts: DealPartnerContact[],
	contactsWithAccess: DealPartnerContact[] | null | undefined
): DealPartnerContact[] {
	const list = [] as DealPartnerContact[];

	if (partnerContacts && partnerContacts.length > 0) {
		partnerContacts.map(member => {
			const find = contactsWithAccess?.find(m => m.guid == member.guid);
			if (!find) {
				list.push(member);
			}
		});
	}

	return list;
}
